import {inject, Injectable} from '@angular/core';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {OnboardingGuide} from '@src/app/core/models/onboarding-guide.model';
import {ApiService} from '@src/app/shared/services/api.service';
import {merge, Observable, of, Subject} from 'rxjs';
import {catchError, filter, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {AuthService} from './auth.service';

@Injectable({providedIn: 'root'})
export class OnboardingGuideService {
    private readonly api = inject(ApiService);
    private readonly auth = inject(AuthService);
    private readonly router = inject(Router);
    private readonly refreshes = new Subject<void>();

    // Subscribe when presenting the guide. Navigation cancels stale tenant requests;
    // failures hide optional guidance without interrupting the surveying workflow.
    public readonly guide$: Observable<OnboardingGuide | null> = merge(
        of(null),
        this.refreshes,
        this.router.events.pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd)),
    ).pipe(
        switchMap(event => event instanceof NavigationStart ? of(null) : this.load()),
        shareReplay({bufferSize: 1, refCount: true}),
    );

    public refresh(): void {
        this.refreshes.next();
    }

    public setHidden(hidden: boolean): Observable<OnboardingGuide | null> {
        return this.save({hidden});
    }

    public present(): Observable<{guide: OnboardingGuide | null; claimed: boolean}> {
        const context = this.context();
        if (!context) return of({guide: null, claimed: false});
        return this.api.patch('onboarding/guide', {presented: true}).pipe(
            map(response => ({guide: this.inContext(response.data, context), claimed: response.auto_open_claimed === true})),
        );
    }

    public selectSite(siteId: number | null): Observable<OnboardingGuide | null> {
        return this.save({site_id: siteId});
    }

    private load(): Observable<OnboardingGuide | null> {
        const context = this.context();
        if (!context) return of(null);
        return this.api.get('onboarding/guide').pipe(
            map(response => this.inContext(response.data, context)),
            catchError(() => of(null)),
        );
    }

    private save(preferences: {hidden?: boolean; site_id?: number | null}): Observable<OnboardingGuide | null> {
        const context = this.context();
        if (!context) return of(null);
        return this.api.patch('onboarding/guide', preferences).pipe(
            map(response => this.inContext(response.data, context)),
            tap(() => this.refresh()),
        );
    }

    private context(): string | null {
        const tenantId = this.auth.tenant?.id;
        const userId = this.auth.user?.id;
        return tenantId && userId ? `${tenantId}:${userId}` : null;
    }

    private inContext(guide: OnboardingGuide, context: string): OnboardingGuide | null {
        return context === this.context() && `${guide.tenant_id}:${guide.user_id}` === context ? guide : null;
    }
}
